import React, { useEffect, useState } from 'react';
import { get, post } from '../../utils/fetchAPI';
import { useParams, useNavigate } from 'react-router-dom';
import PageTitle from "../../components/page-title/pageTitle";
import { RiShieldLine } from "react-icons/ri";
import Loader from "../../components/loader/loder";
import { toast } from "react-toastify";
import { FaCheck } from 'react-icons/fa';

const RolePermissions = () => {
    const { roleId } = useParams();
    const navigate = useNavigate();
    const [role, setRole] = useState(null);
    const [allPermissions, setAllPermissions] = useState([]);
    const [groupedPermissions, setGroupedPermissions] = useState({});
    const [assignedPermissionIds, setAssignedPermissionIds] = useState([]);
    const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        fetchRoleDetails();
        fetchAllPermissions();
        fetchAssignedPermissions();
    }, [roleId]);

    useEffect(() => {
        // Initialize selected permissions with already assigned ones
        setSelectedPermissionIds([...assignedPermissionIds]);
    }, [assignedPermissionIds]);

    const fetchRoleDetails = async () => {
        try {
            const response = await get(`/role/${roleId}`);
            setRole(response?.data);
        } catch (error) {
            console.error('Error fetching role details:', error);
            toast.error('Failed to fetch role details');
        }
    };

    const fetchAllPermissions = async () => {
        setIsLoading(true);
        try {
            const response = await get('/permission/list');
            const permissionsList = response?.data?.permissions || [];
            setAllPermissions(permissionsList);

            // Group permissions by module
            const grouped = permissionsList.reduce((acc, permission) => {
                const module = permission.module || 'other';
                if (!acc[module]) {
                    acc[module] = [];
                }
                acc[module].push(permission);
                return acc;
            }, {});
            setGroupedPermissions(grouped);
        } catch (error) {
            console.error('Error fetching permissions:', error);
            toast.error('Failed to fetch permissions');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAssignedPermissions = async () => {
        try {
            const response = await get(`/role/${roleId}/permissions`);
            const assignedIds = (response?.data?.permissions || []).map(p => p._id);
            setAssignedPermissionIds(assignedIds);
        } catch (error) {
            console.error('Error fetching assigned permissions:', error);
            // If no permissions assigned, set empty array
            setAssignedPermissionIds([]);
        }
    };

    const handlePermissionToggle = (permissionId) => {
        setSelectedPermissionIds(prev => {
            if (prev.includes(permissionId)) {
                return prev.filter(id => id !== permissionId);
            } else {
                return [...prev, permissionId];
            }
        });
    };

    const handleSelectAllModule = (module) => {
        const modulePermissions = groupedPermissions[module] || [];
        const modulePermissionIds = modulePermissions.map(p => p._id);
        
        // Check if all permissions in module are selected
        const allSelected = modulePermissionIds.every(id => selectedPermissionIds.includes(id));

        setSelectedPermissionIds(prev => {
            if (allSelected) {
                // Deselect all permissions in this module
                return prev.filter(id => !modulePermissionIds.includes(id));
            } else {
                // Select all permissions in this module
                const newSelected = [...prev];
                modulePermissionIds.forEach(id => {
                    if (!newSelected.includes(id)) {
                        newSelected.push(id);
                    }
                });
                return newSelected;
            }
        });
    };

    const isModuleAllSelected = (module) => {
        const modulePermissions = groupedPermissions[module] || [];
        if (modulePermissions.length === 0) return false;
        const modulePermissionIds = modulePermissions.map(p => p._id);
        return modulePermissionIds.every(id => selectedPermissionIds.includes(id));
    };

    const handleAssignPermissions = async () => {
        setIsSaving(true);
        try {
            const payload = {
                roleId: roleId,
                permissionIds: selectedPermissionIds
            };

            await post('/role-permission/sync', payload);
            toast.success('Permissions synced successfully!', {
                position: "top-center",
                theme: "light",
            });
            // Refresh assigned permissions
            await fetchAssignedPermissions();
        } catch (error) {
            console.error('Error syncing permissions:', error);
            toast.error('Failed to sync permissions. Please try again.', {
                position: "top-center",
                theme: "light",
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-full">
                <Loader />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-5 w-full px-2 sm:px-0">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <PageTitle title={`Assign Permissions - ${role?.name || 'Role'}`} icon={<RiShieldLine />} />
                    {role && (
                        <div className="mt-2 text-sm text-gray-600">
                            <span className="font-medium">Type:</span> {role.type} | 
                            <span className="font-medium ml-2">Status:</span> {role.status}
                        </div>
                    )}
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => navigate('/roles')}
                        className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                    >
                        Back to Roles
                    </button>
                    <button
                        onClick={handleAssignPermissions}
                        disabled={isSaving}
                        className="px-4 py-2 bg-primary text-white rounded-md hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {isSaving ? 'Saving...' : 'Update Permissions'}
                        {!isSaving && <FaCheck />}
                    </button>
                </div>
            </div>

            {/* Permissions List Grouped by Module */}
            {Object.keys(groupedPermissions).length === 0 ? (
                <div className="bg-white rounded-md p-10 text-center drop-shadow-[0_0px_20px_rgba(203,229,255,0.3)]">
                    <p className="text-gray-500 text-lg">No permissions available</p>
                </div>
            ) : (
                <div className="flex flex-col gap-6">
                    {Object.keys(groupedPermissions).sort().map((module) => {
                        const modulePermissions = groupedPermissions[module] || [];
                        const allModuleSelected = isModuleAllSelected(module);
                        
                        return (
                            <div key={module} className="bg-white rounded-md drop-shadow-[0_0px_20px_rgba(203,229,255,0.3)] overflow-hidden">
                                {/* Module Header */}
                                <div className="bg-gradient-to-r from-primary to-teal-600 px-5 py-3 flex justify-between items-center">
                                    <h2 className="text-white font-bold text-lg capitalize">
                                        {module} Module
                                        <span className="ml-2 text-sm font-normal opacity-90">
                                            ({modulePermissions.length} {modulePermissions.length === 1 ? 'permission' : 'permissions'})
                                        </span>
                                    </h2>
                                    <button
                                        onClick={() => handleSelectAllModule(module)}
                                        className={`px-4 py-1.5 text-sm rounded-md font-medium transition-colors ${
                                            allModuleSelected
                                                ? 'bg-white text-primary hover:bg-gray-100'
                                                : 'bg-teal-700 text-white hover:bg-teal-800'
                                        }`}
                                    >
                                        {allModuleSelected ? 'Deselect All' : 'Select All'}
                                    </button>
                                </div>

                                {/* Permissions List */}
                                <div className="p-4">
                                    <div className="flex flex-col gap-2">
                                        {modulePermissions.map((permission) => {
                                            const isSelected = selectedPermissionIds.includes(permission._id);
                                            const wasAssigned = assignedPermissionIds.includes(permission._id);
                                            
                                            return (
                                                <label
                                                    key={permission._id}
                                                    className={`flex items-center gap-3 p-3 rounded-md border-2 cursor-pointer transition-all ${
                                                        isSelected
                                                            ? 'border-primary bg-teal-50'
                                                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                                    } ${wasAssigned && !isSelected ? 'bg-yellow-50 border-yellow-300' : ''}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => handlePermissionToggle(permission._id)}
                                                        className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                                                    />
                                                    <div className="flex-1">
                                                        <div className="font-medium text-gray-900">
                                                            {permission.name}
                                                            {wasAssigned && (
                                                                <span className="ml-2 text-xs px-2 py-0.5 bg-yellow-200 text-yellow-800 rounded">
                                                                     Assigned
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {permission.slug}
                                                        </div>
                                                    </div>
                                                    <div className="text-sm text-gray-400">
                                                        {permission.status}
                                                    </div>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Summary */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <p className="text-sm text-blue-800">
                    <span className="font-medium">Selected:</span> {selectedPermissionIds.length} permission(s) | 
                    <span className="font-medium ml-2">Assigned:</span> {assignedPermissionIds.length} permission(s)
                </p>
            </div>
        </div>
    );
};

export default RolePermissions;

